import type { HexMapState } from '../engine/render/renderer';

export enum MusicTrack {
    Track1 = 'track1',
    Track2 = 'track2',
}

export enum SoundEffect {
    ChurchBell = 'churchbell',
}

export enum HitSound {
    Hit01 = 'hit01',
    Hit02 = 'hit02',
    Hit03 = 'hit03',
    Hit04 = 'hit04',
    Hit05 = 'hit05',
    Hit06 = 'hit06',
    Hit07 = 'hit07',
    Hit08 = 'hit08',
    Hit09 = 'hit09',
    Hit10 = 'hit10',
}

const MUSIC_SOURCES: Record<MusicTrack, string> = {
    [MusicTrack.Track1]: '/music/track1.mp3',
    [MusicTrack.Track2]: '/music/track2.mp3',
};

const EFFECT_SOURCES: Record<SoundEffect, string> = {
    [SoundEffect.ChurchBell]: '/music/churchbell.flac',
};

const HIT_SOURCES: Record<HitSound, string> = {
    [HitSound.Hit01]: '/music/hit01.mp3.flac',
    [HitSound.Hit02]: '/music/hit02.mp3.flac',
    [HitSound.Hit03]: '/music/hit03.mp3.flac',
    [HitSound.Hit04]: '/music/hit04.mp3.flac',
    [HitSound.Hit05]: '/music/hit05.mp3.flac',
    [HitSound.Hit06]: '/music/hit06.mp3.flac',
    [HitSound.Hit07]: '/music/hit07.mp3.flac',
    [HitSound.Hit08]: '/music/hit08.mp3.flac',
    [HitSound.Hit09]: '/music/hit09.mp3.flac',
    [HitSound.Hit10]: '/music/hit10.mp3.flac',
};

const HIT_LIST = Object.values(HitSound);

type AudioFactory = (src: string) => HTMLAudioElement;

type AudioState = {
    backgroundEnabled: boolean;
    backgroundVolume: number;
    bellEnabled: boolean;
    bellVolume: number;
    hitEnabled: boolean;
    hitVolume: number;
};

export class SoundManager {
    private createAudio: AudioFactory;
    private background: HTMLAudioElement | null = null;
    private backgroundIndex = 0;
    private hitLoop: HTMLAudioElement | null = null;
    private errorAlertIds = new Set<string>();
    private unlocked = false;
    private audioState: AudioState = {
        backgroundEnabled: false,
        backgroundVolume: 0.5,
        bellEnabled: true,
        bellVolume: 0.8,
        hitEnabled: true,
        hitVolume: 0.6,
    };

    constructor(createAudio?: AudioFactory) {
        this.createAudio = createAudio ?? ((src) => new Audio(src));
    }

    sync(state: HexMapState): void {
        const audio = state.config.audio;
        const nextState: AudioState = {
            backgroundEnabled: audio.background.enabled,
            backgroundVolume: audio.background.volume,
            bellEnabled: audio.bell.enabled,
            bellVolume: audio.bell.volume,
            hitEnabled: audio.hit.enabled,
            hitVolume: audio.hit.volume,
        };
        this.audioState = nextState;
        if (!this.unlocked) return;
        this.updateBackground();
        this.updateAlerts(state);
    }

    unlock(): void {
        if (this.unlocked) return;
        this.unlocked = true;
        const audio = this.createAudio(MUSIC_SOURCES[MusicTrack.Track1]);
        audio.volume = 0;
        audio
            .play()
            .then(() => {
                audio.pause();
                audio.currentTime = 0;
            })
            .catch(() => undefined);
        this.updateBackground();
    }

    private updateBackground(): void {
        if (!this.audioState.backgroundEnabled) {
            this.stopBackground();
            return;
        }
        if (!this.background) {
            this.playBackgroundTrack(MusicTrack.Track1);
            return;
        }
        this.background.volume = this.audioState.backgroundVolume;
    }

    private updateAlerts(state: HexMapState): void {
        if (!this.unlocked) return;
        const errorAlerts = state.alerts.filter((alert) => alert.level === 'error');
        const errorIds = new Set(errorAlerts.map((alert) => alert.id));
        const hasErrors = errorAlerts.length > 0;
        const newErrors = errorAlerts.filter((alert) => !this.errorAlertIds.has(alert.id));

        if (newErrors.length > 0 && this.audioState.bellEnabled) {
            this.playEffect(SoundEffect.ChurchBell, this.audioState.bellVolume);
        }

        if (hasErrors && this.audioState.hitEnabled) {
            this.ensureHitLoop();
        } else {
            this.stopHitLoop();
        }

        this.errorAlertIds = errorIds;
    }

    private playBackgroundTrack(track: MusicTrack): void {
        const audio = this.createAudio(MUSIC_SOURCES[track]);
        audio.volume = this.audioState.backgroundVolume;
        audio.loop = false;
        audio.onended = () => {
            if (!this.audioState.backgroundEnabled) return;
            this.backgroundIndex = (this.backgroundIndex + 1) % 2;
            const nextTrack = this.backgroundIndex === 0 ? MusicTrack.Track1 : MusicTrack.Track2;
            this.playBackgroundTrack(nextTrack);
        };
        audio.play().catch(() => undefined);
        this.background = audio;
    }

    private stopBackground(): void {
        if (!this.background) return;
        this.background.pause();
        this.background.currentTime = 0;
        this.background.onended = null;
        this.background = null;
    }

    private ensureHitLoop(): void {
        if (this.hitLoop) {
            this.hitLoop.volume = this.audioState.hitVolume;
            return;
        }
        this.playNextHit();
    }

    private playNextHit(): void {
        if (!this.audioState.hitEnabled) return;
        const hit = HIT_LIST[Math.floor(Math.random() * HIT_LIST.length)] as HitSound;
        const audio = this.createAudio(HIT_SOURCES[hit]);
        audio.volume = this.audioState.hitVolume;
        audio.loop = false;
        audio.onended = () => {
            if (!this.audioState.hitEnabled) return;
            this.playNextHit();
        };
        audio.play().catch(() => undefined);
        this.hitLoop = audio;
    }

    private stopHitLoop(): void {
        if (!this.hitLoop) return;
        this.hitLoop.pause();
        this.hitLoop.currentTime = 0;
        this.hitLoop.onended = null;
        this.hitLoop = null;
    }

    private playEffect(effect: SoundEffect, volume: number): void {
        const audio = this.createAudio(EFFECT_SOURCES[effect]);
        audio.volume = volume;
        audio.play().catch(() => undefined);
    }
}
